/**
 * The board a row action writes to: this one, or another free-form board of
 * the organization the person can edit. Record and briefs boards are left out,
 * since their rows are the platform's records, and so are boards shared to
 * view only, which the server refuses as a target.
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@monksflow/monks-ui';

import { boardTypeOf, organizationTrackersQueryOptions } from '@/entities/tracker';

// eslint-disable-next-line i18next/no-literal-string -- select value id for "this board"
const THIS_BOARD = '__this__';

interface AutomationTargetBoardSelectProps {
  trackerId: string;
  value: string | undefined;
  /** A move needs somewhere else to go; a create or copy can stay on this board. */
  allowThisBoard: boolean;
  onChange: (targetTrackerId: string | undefined) => void;
}

export function AutomationTargetBoardSelect({
  trackerId,
  value,
  allowThisBoard,
  onChange,
}: AutomationTargetBoardSelectProps) {
  const { t } = useTranslation('trackers');
  const { data: boards = [], isPending, isError } = useQuery(organizationTrackersQueryOptions());
  const others = boards.filter(
    (board) =>
      board.id !== trackerId &&
      boardTypeOf(board.itemSource) === 'free_form' &&
      board.accessLevel === 'edit',
  );
  // A saved board that has since been deleted or reshared to view stays
  // selected, named as unavailable, rather than leaving the picker blank.
  const unavailable =
    !isPending && !isError && !!value && !others.some((board) => board.id === value);

  return (
    <Select
      value={value ?? (allowThisBoard ? THIS_BOARD : '')}
      onValueChange={(next) => onChange(next === THIS_BOARD ? undefined : next)}
    >
      <SelectTrigger
        className="h-9 flex-1"
        aria-label={t('trackersBoard.automationRuleFields.targetBoard')}
      >
        <SelectValue placeholder={t('trackersBoard.automationRuleFields.pickABoard')} />
      </SelectTrigger>
      <SelectContent>
        {allowThisBoard ? (
          <SelectItem value={THIS_BOARD}>
            {t('trackersBoard.automationRuleFields.thisBoard')}
          </SelectItem>
        ) : null}
        {others.map((board) => (
          <SelectItem key={board.id} value={board.id}>
            {board.workspaceName
              ? t('trackersBoard.automationRuleFields.boardInWorkspace', {
                  name: board.name,
                  workspace: board.workspaceName,
                })
              : board.name}
          </SelectItem>
        ))}
        {unavailable ? (
          <SelectItem value={value} disabled>
            {t('trackersBoard.automationRuleFields.boardUnavailable')}
          </SelectItem>
        ) : null}
        {isPending || isError ? (
          // eslint-disable-next-line i18next/no-literal-string -- select value id for a status line
          <SelectItem value="__status__" disabled>
            {isPending
              ? t('trackersBoard.automationRuleFields.loadingBoards')
              : t('trackersBoard.automationRuleFields.boardsFailed')}
          </SelectItem>
        ) : null}
      </SelectContent>
    </Select>
  );
}
